import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {EyeCircleFilled} from '@fluentui/react-icons';

import {
	CommandBarButtonCompat as CommandBarButton,
	IButtonStyles,
	IIconProps,
	TooltipHostCompat as TooltipHost,
	useTheme,
} from 'components/FluentUICompat';

interface LoadAllButtonProps {
	loadAll: () => Promise<void>;
	tooltipText: string;
	isLoading?: boolean;
	hasBeenClicked?: boolean;
}

export const LoadAllButton: React.FC<LoadAllButtonProps> = ({
	loadAll,
	tooltipText,
	isLoading,
	hasBeenClicked,
}) => {
	const theme = useTheme();
	const {t} = useTranslation('hooks/useLoadAllCommand');

	const styles: IButtonStyles = useMemo(() => {
		const isDisabled = isLoading || hasBeenClicked;
		if (isLoading || isDisabled) {
			return {
				root: {
					height: '100%',
					backgroundColor: theme.palette.themePrimary,
				},
				icon: {color: theme.palette.white, fontSize: '24px'},
				rootDisabled: {
					backgroundColor: theme.palette.neutralQuaternaryAlt,
					opacity: 1,
				},
				iconDisabled: {color: theme.palette.white, fontSize: '24px'},
				label: {
					color: theme.palette.white,
					flexDirection: 'column',
				},
				labelDisabled: {
					color: theme.palette.neutralPrimary,
				},
			};
		}
		return {
			root: {height: '100%'},
			icon: {fontSize: '24px'},
			label: {
				flexDirection: 'column',
			},
		};
	}, [isLoading, hasBeenClicked, theme]);

	const isDisabled = isLoading || hasBeenClicked;

	return (
		<TooltipHost content={tooltipText}>
			<CommandBarButton
				onRenderIcon={() => (
					<EyeCircleFilled
						style={{
							fontSize: '24px',
							color: isDisabled
								? theme.palette.neutralTertiary
								: theme.palette.redDark,
						}}
					/>
				)}
				text={t('LoadAllButtonLabel')}
				styles={styles}
				onClick={loadAll}
				disabled={isDisabled}
				ariaLabel={tooltipText}
			/>
		</TooltipHost>
	);
};
